import { ReactNode, useMemo } from 'react';
import styled, { DefaultTheme } from 'styled-components';
import { Collapse, CollapsePanelProps } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';

import { getFaqItems } from '../../common/helpers/faq.utils';
import useI18n from '../../common/hooks/useI18n';

const { Panel } = Collapse;

type Props = {
  spreadsheetName: string;
  query: string;
  theme: DefaultTheme;
  className?: string;
};

const Title = styled.h2`
  ${(props) => props.theme.font.h2}
  color: ${(props) => props.theme.colors.secondary};
`;

const Divider = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;
  border-top: 1px solid #e0e0e0;
  background-color: #e0e0e0;
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 20px;
`;

const Faq: React.FC<Props> = ({
  spreadsheetName,
  query,
  className,
  theme,
}): JSX.Element => {
  const i18n = useI18n();

  const title = useMemo(
    () =>
      i18n.t(`dynamic.${spreadsheetName}.${query}.faqTitle`) ||
      i18n.t('faq.title'),
    [i18n, query, spreadsheetName]
  );

  const faqItems = useMemo(() => getFaqItems(i18n, spreadsheetName, query), [
    i18n,
    spreadsheetName,
    query,
  ]);

  const genExtra = (panelProps: CollapsePanelProps): ReactNode => {
    const style = { color: theme.colors.primary };
    return panelProps['isActive'] ? (
      <MinusOutlined style={style} />
    ) : (
      <PlusOutlined style={style} />
    );
  };

  const panels = faqItems
    .map((item) => {
      const titleElement = <h3>{item.title}</h3>;
      return (
        <Panel header={titleElement} key={item.title.substring(0, 10)}>
          <Description>{item.description}</Description>
        </Panel>
      );
    })
    .filter((section) => !!section);

  return (
    <div className={className}>
      <Title
        key={title.substring(0, 10) + new Date().getTime()}
        as="h2"
        dangerouslySetInnerHTML={{
          __html: title,
        }}
      />
      <Divider />

      <Collapse expandIcon={genExtra} expandIconPosition="end">
        {panels}
      </Collapse>
    </div>
  );
};

export default styled(Faq)`
  position: relative;

  .ant-collapse {
    background-color: unset;

    .ant-collapse-header {
      font-weight: 600;
    }
  }
`;
